import type {
  NoticeCreateRequest,
  NoticeItemResponse,
  NoticeListItemResponse,
  NoticeListQuery,
  NoticeUpdateRequest
} from '@lmc/validations'
import { getJSON, postJSON } from 'common/services/api'
import { errorToastMeta } from 'common/services/queryClient'
import qs from 'qs'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { NoticeFormValues } from '../components/NoticeForm'
import type { NoticeboardSortObject } from '../components/NoticesTable'

export const GET_NOTICES = `/api/list/notices`

export enum NOTICES_FETCH_TYPE {
  SINGLE = 'SINGLE',
  LIST = 'MULTIPLE'
}

export type NoticeboardListFilter = 'unread' | 'all'

/** Query key on `/noticeboard` and `/noticeboard/:id` so back navigation keeps the list mode. */
export const NOTICE_LIST_FILTER_QUERY_KEY = 'filter'

export function noticeFilterFromSearchParams(
  searchParams: URLSearchParams
): NoticeboardListFilter {
  const raw = searchParams.get(NOTICE_LIST_FILTER_QUERY_KEY)
  if (raw === 'all') {
    return 'all'
  }
  return 'unread'
}

type CommonProps<Type extends NOTICES_FETCH_TYPE> = {
  onCreateSuccess?: () => void
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  page?: number
  fetch: Type
  sortParams?: NoticeboardSortObject
  noticeFilter?: NoticeboardListFilter
}

type UseNoticesProps<Type extends NOTICES_FETCH_TYPE> = CommonProps<Type> &
  (Type extends NOTICES_FETCH_TYPE.SINGLE
    ? {
        noticeID: string | undefined // Required when Type is 'SINGLE'
        title?: undefined
      }
    : {
        noticeID?: undefined // Optional when Type is 'MULTIPLE'
        title?: string
      })

type CommonReturn = {
  isLoading: boolean
  isNoticeLoading: boolean
  createNotice: (values: NoticeFormValues) => void
  deleteNotice: () => void
  updateNotice: (values: NoticeFormValues) => void
  refetch: () => void
  count?: number
}

export type UseNoticesReturnOne = CommonReturn & {
  result: NoticeItemResponse | undefined
}

export type UseNoticesReturnMany = CommonReturn & {
  result: NoticeListItemResponse[] | undefined
}

type NoticesData<T> = { result: T[]; count?: number }

const useNotices = <Type extends NOTICES_FETCH_TYPE>({
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  fetch,
  noticeID,
  title,
  page,
  sortParams,
  noticeFilter
}: UseNoticesProps<Type>): UseNoticesReturnOne | UseNoticesReturnMany => {
  const listQueryParams = qs.stringify({
    title,
    ...(noticeFilter === 'unread' ? { noticeFilter: 'unread' } : {}),
    pagination: {
      page: page || 1,
      sort: sortParams
    }
  } as NoticeListQuery)

  const {
    isLoading: areNoticesLoading,
    data: noticesData,
    refetch: refetchNotices
  } = useQuery<NoticesData<NoticeListItemResponse>>(
    ['notices', title, page, sortParams, noticeFilter],
    () => getJSON({ url: `${GET_NOTICES}?${listQueryParams}` }),
    {
      enabled: fetch === NOTICES_FETCH_TYPE.LIST,
      meta: errorToastMeta
    }
  )
  const {
    data: noticeData,
    isLoading: isNoticeLoading,
    refetch: refetchNotice
  } = useQuery<{ result: NoticeItemResponse }>(
    ['notice', noticeID],
    () => getJSON({ url: `${GET_NOTICES}/${noticeID}` }),
    {
      enabled: fetch === NOTICES_FETCH_TYPE.SINGLE || noticeID !== undefined,
      onSuccess: () => {
        queryClient.invalidateQueries('notices')
      },
      meta: errorToastMeta
    }
  )

  const createFunction = (values: NoticeCreateRequest) =>
    postJSON({
      url: `list/notices`,
      method: 'POST',
      json: values
    })

  const updateFunction = (values: NoticeUpdateRequest) =>
    postJSON({
      url: `list/notices/${noticeID}`,
      method: 'PATCH',
      json: values
    })

  const deleteFunction = () =>
    postJSON({
      url: `list/notices/${noticeID}`,
      method: 'DELETE'
    })

  const queryClient = useQueryClient()

  // `mutate`, not `mutateAsync`: callers fire and forget, and an unawaited
  // `mutateAsync` rejection becomes an unhandled promise rejection (CM-2340).
  // Failures reach the user through `errorToastMeta` instead.
  const { mutate: createMutation } = useMutation<
    NoticeItemResponse,
    unknown,
    NoticeCreateRequest
  >(['create-notice'], createFunction, {
    meta: errorToastMeta,
    onSuccess: () => {
      onCreateSuccess?.()
      refetchHandler()
    }
  })

  const { mutate: updateMutation } = useMutation<
    NoticeItemResponse,
    unknown,
    NoticeUpdateRequest
  >([`update-notice-${noticeID}`], updateFunction, {
    meta: errorToastMeta,
    onSuccess: () => {
      onUpdateSuccess?.()
      refetchHandler()
    }
  })

  const { mutate: deleteMutation } = useMutation(
    [`delete-notice`, noticeID],
    deleteFunction,
    {
      meta: errorToastMeta,
      onSuccess: () => {
        onDeleteSuccess?.()
        refetchNotices()
      }
    }
  )

  const refetchHandler = () => {
    switch (fetch) {
      case NOTICES_FETCH_TYPE.LIST:
        queryClient.invalidateQueries('notices')
        refetchNotices()
        break
      default:
        queryClient.invalidateQueries('notice')
        refetchNotice()
    }
  }

  if (fetch === NOTICES_FETCH_TYPE.SINGLE) {
    return {
      isNoticeLoading,
      isLoading: areNoticesLoading,
      result: noticeData?.result,
      createNotice: createMutation,
      deleteNotice: deleteMutation,
      updateNotice: updateMutation,
      refetch: refetchHandler
    }
  }
  return {
    isNoticeLoading,
    isLoading: areNoticesLoading,
    result: noticesData?.result,
    count: noticesData?.count,
    createNotice: createMutation,
    deleteNotice: deleteMutation,
    updateNotice: updateMutation,
    refetch: refetchHandler
  }
}

export default useNotices
